import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import { listRollouts } from './api';

// Section embedded on a brand-type Account detail page (spec §7.5-ish / P2b
// chunk 1 scope). Shows the brand's rollouts + a "New rollout" CTA.

function statusBadge(status: string): string {
  switch (status) {
    case 'draft':
      return 'bg-slate-100 text-slate-700';
    case 'active':
      return 'bg-emerald-100 text-emerald-800';
    case 'archived':
      return 'bg-slate-200 text-slate-500';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

export function RolloutsForBrand({ brandAccountId }: { brandAccountId: string }) {
  const rollouts = useQuery({
    queryKey: ['rollouts', brandAccountId],
    queryFn: () => listRollouts({ brandAccountId }),
  });

  if (rollouts.isLoading) {
    return (
      <div className="py-3 flex items-center gap-2 text-sm text-slate-500">
        <Spinner /> Loading rollouts…
      </div>
    );
  }
  if (rollouts.error) {
    return (
      <p className="text-sm text-red-600">
        Failed to load rollouts: {(rollouts.error as Error).message}
      </p>
    );
  }

  const list = rollouts.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Link to={`/rollouts/new?brand=${encodeURIComponent(brandAccountId)}`}>
          <Button variant="outline">New rollout</Button>
        </Link>
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-slate-500">No rollouts yet for this brand.</p>
      ) : (
        <ul className="divide-y divide-slate-100 text-sm">
          {list.map((r) => {
            const start = r.defaultStartDate ? new Date(r.defaultStartDate).toLocaleDateString() : '—';
            const end = r.defaultEndDate ? new Date(r.defaultEndDate).toLocaleDateString() : '—';
            return (
              <li key={r.id} className="py-2 flex items-center justify-between">
                <div>
                  <Link to={`/rollouts/${r.id}`} className="font-medium text-slate-900 hover:underline">
                    {r.name}
                  </Link>
                  <span className="text-xs text-slate-500 ml-2">
                    {start} → {end}
                  </span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadge(r.status)}`}>
                  {r.status}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
